"use strict";
/**
 * `Headers` combinator: sets headers on a `Req` and returns a `Req`.
 *
 * **Warning:** the merging logic had to be "reversed" because of the contravariant nature of `Reader` and because the execution of combinators is from right to left (_function composition_).
 *
 * This leads to a "weird" behavior for which the headers provided when `Req` is run win over the ones set with the combinator.
 *
 * So, for example, if we have:
 * ```ts
 * const request = pipe(appy.get, withHeaders({'X-Foo': 'bar'}), withHeaders({'X-Foo': 'baz'}));
 *
 * request(['http://some.endpoint', {headers: {'X-Foo': 'foo'}}])
 * ```
 * when `request` is ran the underlying `fetch` call will be made with a `X-Foo = 'foo'` header.
 *
 * @since 3.0.0
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.withHeaders = void 0;
var RTE = __importStar(require("fp-ts/ReaderTaskEither"));
var Record_1 = require("fp-ts/Record");
var Semigroup_1 = require("fp-ts/Semigroup");
var TU = __importStar(require("fp-ts/Tuple"));
var function_1 = require("fp-ts/function");
var request_1 = require("../request");
var RML = (0, Record_1.getMonoid)((0, Semigroup_1.last)());
/**
 * Merges provided `Headers` with `Req` ones and returns the updated `Req`.
 *
 * @category combinators
 * @since 3.0.0
 */
var withHeaders = function (headers) {
    return RTE.local(function (input) {
        return (0, function_1.pipe)((0, request_1.normalizeReqInput)(input), TU.mapSnd(function (init) { return merge(init, headers); }));
    });
};
exports.withHeaders = withHeaders;
var merge = function (init, h) {
    // The "weird" `concat` is due to the mix of the contravariant nature of `Reader`
    // and the function composition at the base of "combinators".
    // Because combinators are applied from right to left, the merging has to be "reversed".
    // This leads to another "weird" behavior for which the `Headers` provided when `Req` is run
    // wins over the ones set with the combinator.
    var headers = typeof init.headers === 'undefined' ? toRecord(h) : concat(h, init.headers);
    return __assign(__assign({}, init), { headers: headers });
};
var concat = function (a, b) {
    return RML.concat(toRecord(a), toRecord(b));
};
var toRecord = function (h) {
    if (Array.isArray(h)) {
        return h.reduce(function (acc, _a) {
            var _b;
            var k = _a[0], v = _a[1];
            return (__assign(__assign({}, acc), (_b = {}, _b[k] = v, _b)));
        }, {});
    }
    if (h instanceof Headers) {
        var result_1 = {};
        h.forEach(function (v, k) { return (result_1[k] = v); });
        return result_1;
    }
    return h;
};
//# sourceMappingURL=headers.js.map